/**
 * Route utility functions.
 *
 * Most functions have been centralized in utils/routeStopOperations/.
 * This file retains isOnRequestStop (core dependency for the operations module)
 * and re-exports moved functions for backward compatibility.
 */
import { PRICED_SERVICE_TYPE, RECURRENCE, RECURRENCE_V2, CONFIGURED_SERVICE_EVENT_TYPE } from 'settings/constants/service'

const { ONE_OFF } = CONFIGURED_SERVICE_EVENT_TYPE

export const canModifyRouteSequence = () => {
  return true
}

/**
 * Returns true when a stop/row/work-order should be excluded from master-sequence
 * operations. That covers two distinct cases:
 *   1. The parent configured service is "On Request" (no recurring schedule exists).
 *   2. The individual work order / event is a "One Off" — a single-date event added
 *      to an otherwise recurring service. These are editable on the Events accordion
 *      (see `selectIsOneOff`) precisely because they aren't part of the master
 *      sequence, so bulk/move/schedule flows must treat them the same way.
 *
 * We check the strongest signal — `configuredServiceEventType === 'One Off'` — first
 * because a recurring configured service can carry one-off WOs, and in that scenario
 * `pricedServiceType` / `serviceType` still say "Recurring".
 *
 * Field sources across endpoints:
 *   - configuredServiceEventType: "Recurring" | "One Off"  (RouteStop, WorkOrderListView,
 *                                                          WorkOrderDTO, ServiceEvent)
 *   - pricedServiceType:          "On Request"             (route stops / geojson / WO list)
 *   - workOrderType:              "On Request"             (WO details — per-WO override)
 *   - serviceType:                "On Request" / "ON_REQUEST"
 */
export const isOnRequestStop = stop => {
  const configuredServiceEventType = stop?.configuredServiceEventType
  if (typeof configuredServiceEventType === 'string' && configuredServiceEventType.toLowerCase() === ONE_OFF) return true

  const pricedServiceType = stop?.pricedServiceType
  if (pricedServiceType === PRICED_SERVICE_TYPE.ON_REQUEST) return true

  const workOrderType = stop?.workOrderType
  if (workOrderType === RECURRENCE.ON_REQUEST || workOrderType === RECURRENCE_V2.ON_REQUEST) return true

  const serviceType = stop?.serviceType
  if (serviceType === RECURRENCE.ON_REQUEST || serviceType === RECURRENCE_V2.ON_REQUEST) return true

  return false
}

/**
 * A stop can't land on a date that already holds the same *occurrence*, so the match is
 * on `configuredServiceEventId`.
 *
 * It used to be on `configuredServiceId`, mirroring the BE `filterDuplicateStops` /
 * `getDuplicateServices` guard, and that is the wrong key on both sides. A
 * configured service belongs to one account and location and legitimately yields several
 * work orders on a date — every on-request job at a site hangs off that site's one
 * standing service — so the service-level match refused moves that were never conflicts.
 * For weekly recurring work the two keys agree: a recurring configured service has
 * exactly one RECURRING event, so CS and CSE are 1:1 and the rule is unchanged there.
 *
 * @param {Object[]} selectedSourceStops - rows the user wants to move
 * @param {Object[]} existingTargetStops - rows already on the target date
 * @returns {{ duplicates: Object[], uniques: Object[] }}
 *   - `duplicates`: source stops whose occurrence is already on the target date
 *   - `uniques`:    source stops safe to move
 */
export const findDuplicateStopsOnTargetDate = (selectedSourceStops = [], existingTargetStops = []) => {
  const existingCseIds = new Set((existingTargetStops || []).map(s => s?.configuredServiceEventId).filter(Boolean))
  const duplicates = []
  const uniques = []
  ;(selectedSourceStops || []).forEach(stop => {
    const cseId = stop?.configuredServiceEventId
    if (cseId && existingCseIds.has(cseId)) duplicates.push(stop)
    else uniques.push(stop)
  })
  return { duplicates, uniques }
}

// Backward-compatible re-exports — consumers can import from here or directly from routeStopOperations
export { parseDaysOfWeek } from 'utils/routeStopOperations/recurrence'
export { mapStopToListItemFormat, mapMasterStopToListItemFormat } from 'utils/routeStopOperations/parsers'
export { formatStopFeatures, formatUnassignedStopFeatures } from 'utils/routeStopOperations/featureFormatters'
export { transformResponse } from 'utils/routeStopOperations/transformResponse'
